import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Navigation, Maximize2, Calculator } from 'lucide-react';
import RouteMapModal from '../../RouteMapModal';

const homeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [1, -28], shadowSize: [33, 33]
});

const appointmentIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [20, 33], iconAnchor: [10, 33], popupAnchor: [1, -28], shadowSize: [33, 33]
});

const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [30, 30] });
  }, [bounds, map]);
  return null;
};

const formatTime = (mins) => {
  if (!mins || mins <= 0) return '0 min';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const RouteMapPreview = ({ installerAddress, appointmentAddress, forwardTravelTime, returnTravelTime }) => {
  const [homeCoords, setHomeCoords] = useState(null);
  const [aptCoords, setAptCoords] = useState(null);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addressKey, setAddressKey] = useState('');
  const [showFullRoute, setShowFullRoute] = useState(false);
  const [showCosts, setShowCosts] = useState(false);

  useEffect(() => {
    if (!installerAddress || !appointmentAddress) {
      setHomeCoords(null);
      setAptCoords(null);
      setRoute(null);
      return;
    }

    const key = `${installerAddress}|${appointmentAddress}`;
    if (key === addressKey) return;
    setAddressKey(key);

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const geocode = async (addr) => {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1`,
            { headers: { 'User-Agent': 'LoJackCalendar/1.0' } }
          );
          const data = await res.json();
          if (data?.length > 0) return [parseFloat(data[0].lat), parseFloat(data[0].lon)];
          return null;
        };

        const home = await geocode(installerAddress);
        await new Promise(r => setTimeout(r, 200));
        if (cancelled) return;
        const apt = await geocode(appointmentAddress);
        if (cancelled) return;

        if (!home || !apt) {
          setError('Indirizzi non trovati');
          setLoading(false);
          return;
        }

        setHomeCoords(home);
        setAptCoords(apt);

        await new Promise(r => setTimeout(r, 200));
        if (cancelled) return;

        const routeRes = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${home[1]},${home[0]};${apt[1]},${apt[0]}?overview=full&geometries=geojson`
        );
        const routeData = await routeRes.json();
        if (cancelled) return;

        if (routeData.routes?.length > 0) {
          setRoute(routeData.routes[0].geometry.coordinates.map(c => [c[1], c[0]]));
        }
      } catch {
        if (!cancelled) setError('Errore caricamento mappa');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const timer = setTimeout(load, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [installerAddress, appointmentAddress]);

  if (!installerAddress || !appointmentAddress) return null;

  const bounds = homeCoords && aptCoords ? [homeCoords, aptCoords] : null;

  return (
    <div className="mt-3 rounded-lg overflow-hidden border border-gray-200" data-testid="route-map-preview">
      <div className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b">
        <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
          <Navigation size={12} /> Percorso Tecnico
        </span>
        <div className="flex items-center gap-3 text-xs">
          {forwardTravelTime > 0 && (
            <span className="text-blue-700 font-medium">Andata: ~{formatTime(forwardTravelTime)}</span>
          )}
          {returnTravelTime > 0 && (
            <span className="text-amber-700 font-medium">Ritorno: ~{formatTime(returnTravelTime)}</span>
          )}
        </div>
      </div>

      <div className="relative" style={{ height: '180px' }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
              Caricamento mappa...
            </div>
          </div>
        ) : error ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-xs text-gray-400">
            {error}
          </div>
        ) : homeCoords && aptCoords ? (
          <MapContainer
            center={homeCoords}
            zoom={10}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {bounds && <FitBounds bounds={bounds} />}
            <Marker position={homeCoords} icon={homeIcon}>
              <Popup><span className="text-xs font-medium">Casa Tecnico</span><br /><span className="text-xs text-gray-500">{installerAddress}</span></Popup>
            </Marker>
            <Marker position={aptCoords} icon={appointmentIcon}>
              <Popup><span className="text-xs font-medium">Appuntamento</span><br /><span className="text-xs text-gray-500">{appointmentAddress}</span></Popup>
            </Marker>
            {route && <Polyline positions={route} color="#E31B23" weight={3} opacity={0.7} />}
          </MapContainer>
        ) : null}
      </div>

      <div className="bg-gray-50 px-3 py-1.5 flex items-center justify-between border-t">
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-blue-500 rounded-full inline-block"></span> Casa tecnico
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 bg-red-500 rounded-full inline-block"></span> Appuntamento
          </span>
          <span className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-red-600 inline-block" style={{width:'12px', height:'3px', borderRadius:'1px'}}></span> Percorso
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowCosts(true)}
            className="px-2 py-1 text-xs text-green-700 hover:bg-green-50 rounded transition flex items-center gap-1"
            data-testid="route-map-costs-btn"
          >
            <Calculator size={12} /> Costi
          </button>
          <button
            type="button"
            onClick={() => setShowFullRoute(true)}
            className="px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 rounded transition flex items-center gap-1"
            data-testid="route-map-fullscreen-btn"
          >
            <Maximize2 size={12} /> Indicazioni
          </button>
        </div>
      </div>

      <RouteMapModal
        isOpen={showFullRoute}
        onClose={() => setShowFullRoute(false)}
        startAddress={installerAddress}
        endAddress={appointmentAddress}
        startLabel="Casa Tecnico"
        endLabel="Appuntamento"
      />
      <RouteMapModal
        isOpen={showCosts}
        onClose={() => setShowCosts(false)}
        startAddress={installerAddress}
        endAddress={appointmentAddress}
        startLabel="Casa Tecnico"
        endLabel="Appuntamento"
        showCosts={true}
      />
    </div>
  );
};

export default RouteMapPreview;
